import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Button } from '../ui/button'
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react'
import { toast } from 'sonner'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  level?: 'page' | 'component'
  maxRetries?: number
  enableAutoRetry?: boolean
  showToast?: boolean
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  retryCount: number
  isRetrying: boolean
  lastRetryTime: number | null
}

export class ErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: NodeJS.Timeout | null = null
  private autoRetryTimeoutId: NodeJS.Timeout | null = null

  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    retryCount: 0,
    isRetrying: false,
    lastRetryTime: null
  }

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false,
      lastRetryTime: null
    }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }

    // Report error to external service if available
    this.reportError(error, errorInfo)

    this.setState({
      errorInfo
    })

    // Show toast notification if enabled
    if (this.props.showToast !== false) {
      toast.error(this.getErrorMessage(error), {
        description: this.getErrorDescription(error),
        duration: 5000,
      })
    }

    // Auto-retry if enabled and within limits
    if (this.props.enableAutoRetry && this.state.retryCount < (this.props.maxRetries || 3)) {
      this.scheduleAutoRetry()
    }
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId)
    }
    if (this.autoRetryTimeoutId) {
      clearTimeout(this.autoRetryTimeoutId)
    }
  }

  private reportError = (error: Error, errorInfo: ErrorInfo) => {
    // Send to error reporting service (Sentry, LogRocket, etc.)
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      (window as any).Sentry.captureException(error, {
        extra: {
          errorInfo,
          componentStack: errorInfo.componentStack,
          retryCount: this.state.retryCount,
          level: this.props.level
        }
      })
    }
  }

  private getErrorMessage = (error: Error): string => {
    // Categorize errors and provide user-friendly messages
    const message = error.message?.toLowerCase() || ''

    if (message.includes('network') || message.includes('fetch')) {
      return 'Connection Error'
    }
    if (message.includes('timeout')) {
      return 'Request Timeout'
    }
    if (message.includes('unauthorized') || message.includes('forbidden')) {
      return 'Access Denied'
    }
    if (message.includes('not found')) {
      return 'Resource Not Found'
    }

    return 'Something went wrong'
  }

  private getErrorDescription = (error: Error): string => {
    const message = error.message?.toLowerCase() || ''

    if (message.includes('network') || message.includes('fetch')) {
      return 'Please check your internet connection and try again.'
    }
    if (message.includes('timeout')) {
      return 'The request took too long. Please try again.'
    }
    if (message.includes('unauthorized') || message.includes('forbidden')) {
      return 'You don\'t have permission to access this resource.'
    }
    if (message.includes('not found')) {
      return 'The requested resource could not be found.'
    }

    return 'We encountered an unexpected error. This has been logged and we\'ll look into it.'
  }

  private calculateRetryDelay = (retryCount: number): number => {
    // Exponential backoff: 1s, 2s, 4s, 8s...
    return Math.min(1000 * Math.pow(2, retryCount), 30000) // Max 30 seconds
  }

  private scheduleAutoRetry = () => {
    const delay = this.calculateRetryDelay(this.state.retryCount)
    this.autoRetryTimeoutId = setTimeout(() => {
      this.performAutoRetry()
    }, delay)
  }

  private performAutoRetry = () => {
    this.setState({ isRetrying: true })

    // Show retry toast
    toast.loading(`Retrying... (${this.state.retryCount + 1}/${this.props.maxRetries || 3})`, {
      id: 'error-retry'
    })

    // Reset error state to trigger re-render
    setTimeout(() => {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1,
        isRetrying: false,
        lastRetryTime: Date.now()
      }))

      toast.dismiss('error-retry')
      toast.success('Retrying operation...')
    }, 500)
  }

  private handleRetry = () => {
    // Clear any pending auto-retry
    if (this.autoRetryTimeoutId) {
      clearTimeout(this.autoRetryTimeoutId)
      this.autoRetryTimeoutId = null
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false,
      lastRetryTime: null
    })

    toast.success('Retrying...')
  }

  private handleReload = () => {
    window.location.reload()
  }

  private handleGoHome = () => {
    window.location.href = '/dashboard'
  }

  public render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default error UI
      const isPageLevel = this.props.level === 'page'
      const error = this.state.error!
      const canRetry = this.state.retryCount < (this.props.maxRetries || 3)

      if (isPageLevel) {
        return (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
            <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">
                {this.getErrorMessage(error)}
              </h1>
              <p className="text-sm text-gray-600 mb-4">
                {this.getErrorDescription(error)}
              </p>
              {this.state.retryCount > 0 && (
                <p className="text-xs text-gray-500 mb-4">
                  Retry attempts: {this.state.retryCount}/{this.props.maxRetries || 3}
                </p>
              )}
              <div className="space-y-3">
                {canRetry && (
                  <Button onClick={this.handleRetry} className="w-full" disabled={this.state.isRetrying}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${this.state.isRetrying ? 'animate-spin' : ''}`} />
                    {this.state.isRetrying ? 'Retrying...' : 'Try Again'}
                  </Button>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    onClick={this.handleGoHome}
                    className="flex-1"
                  >
                    <Home className="h-4 w-4 mr-2" />
                    Go Home
                  </Button>
                  <Button
                    variant="outline"
                    onClick={this.handleReload}
                    className="flex-1"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Reload
                  </Button>
                </div>
              </div>
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-4 text-left">
                  <summary className="text-sm text-gray-500 cursor-pointer flex items-center">
                    <Bug className="h-3 w-3 mr-1" />
                    Error Details (Development)
                  </summary>
                  <pre className="mt-2 text-xs text-red-600 whitespace-pre-wrap overflow-auto max-h-32">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              )}
            </div>
          </div>
        )
      }

      // Component-level error UI
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex items-center mb-2">
            <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
            <span className="text-sm font-medium text-red-800">
              {this.getErrorMessage(error)}
            </span>
          </div>
          <p className="text-sm text-red-700 mb-3">
            {this.getErrorDescription(error)}
          </p>
          {canRetry && (
            <Button
              size="sm"
              variant="outline"
              onClick={this.handleRetry}
              disabled={this.state.isRetrying}
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${this.state.isRetrying ? 'animate-spin' : ''}`} />
              {this.state.isRetrying ? 'Retrying...' : 'Retry'}
            </Button>
          )}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details className="mt-2">
              <summary className="text-xs text-red-600 cursor-pointer flex items-center">
                <Bug className="h-3 w-3 mr-1" />
                Error Details
              </summary>
              <pre className="mt-1 text-xs text-red-600 whitespace-pre-wrap">
                {this.state.error.toString()}
              </pre>
            </details>
          )}
        </div>
      )
    }

    return this.props.children
  }
}

// Higher-order component for easier wrapping
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) {
  return function WithErrorBoundaryComponent(props: P) {
    return (
      <ErrorBoundary {...errorBoundaryProps}>
        <Component {...props} />
      </ErrorBoundary>
    )
  }
}

// Hook for error reporting
export function useErrorHandler() {
  return (error: Error, errorInfo?: string) => {
    console.error('Manual error report:', error, errorInfo)

    // In production, you might want to send this to an error reporting service
    // Example: Sentry, LogRocket, etc.
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      (window as any).Sentry.captureException(error, {
        extra: { errorInfo }
      })
    }
  }
}

// Utility function to categorize errors
export function categorizeError(error: Error): {
  category: 'network' | 'auth' | 'validation' | 'server' | 'client' | 'unknown'
  userMessage: string
  technicalMessage: string
} {
  const message = error.message?.toLowerCase() || ''

  if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
    return {
      category: 'network',
      userMessage: 'Connection Error',
      technicalMessage: 'Please check your internet connection and try again.'
    }
  }

  if (message.includes('unauthorized') || message.includes('forbidden') || message.includes('permission')) {
    return {
      category: 'auth',
      userMessage: 'Access Denied',
      technicalMessage: 'You don\'t have permission to perform this action.'
    }
  }

  if (message.includes('validation') || message.includes('invalid') || message.includes('required')) {
    return {
      category: 'validation',
      userMessage: 'Invalid Input',
      technicalMessage: 'Please check your input and try again.'
    }
  }

  if (message.includes('500') || message.includes('server error') || message.includes('internal')) {
    return {
      category: 'server',
      userMessage: 'Server Error',
      technicalMessage: 'Our servers are experiencing issues. Please try again later.'
    }
  }

  if (message.includes('404') || message.includes('not found')) {
    return {
      category: 'server',
      userMessage: 'Not Found',
      technicalMessage: 'The requested resource could not be found.'
    }
  }

  return {
    category: 'unknown',
    userMessage: 'Something went wrong',
    technicalMessage: 'We encountered an unexpected error. This has been logged and we\'ll look into it.'
  }
}

// Enhanced error boundary with auto-retry enabled by default
export function withAutoRetryErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) {
  return function WithAutoRetryErrorBoundaryComponent(props: P) {
    return (
      <ErrorBoundary
        enableAutoRetry={true}
        maxRetries={3}
        showToast={true}
        {...errorBoundaryProps}
      >
        <Component {...props} />
      </ErrorBoundary>
    )
  }
}